import { LEGAL_VERSIONS as _LEGAL_VERSIONS } from '../../supabase/functions/_shared/legal-versions';
import type { LegalReacceptMode as _LegalReacceptMode } from '../../supabase/functions/_shared/legal-versions';

export const LEGAL_VERSIONS = _LEGAL_VERSIONS;
export type ReacceptMode = _LegalReacceptMode;

export type AcceptanceMethod = 'first_run' | 'material_reaccept' | 'manual';

export interface LegalStatus {
  needsFirstRunAcceptance: boolean;
  termsMismatch: boolean;
  privacyMismatch: boolean;
  termsRequiresReaccept: boolean;
  privacyRequiresReaccept: boolean;
  anyMaterialReacceptRequired: boolean;
  anyNoticePending: boolean;
}

export function getLegalStatus(
  profile: {
    terms_accepted_at?: string | null;
    terms_version?: string | null;
    privacy_accepted_at?: string | null;
    privacy_version?: string | null;
  } | null
): LegalStatus {
  if (!profile) {
    return {
      needsFirstRunAcceptance: true,
      termsMismatch: false,
      privacyMismatch: false,
      termsRequiresReaccept: false,
      privacyRequiresReaccept: false,
      anyMaterialReacceptRequired: false,
      anyNoticePending: false,
    };
  }

  const needsFirstRun = !profile.terms_accepted_at || !profile.privacy_accepted_at;
  const termsMismatch = !!profile.terms_accepted_at && (profile.terms_version ?? '') !== LEGAL_VERSIONS.terms.version;
  const privacyMismatch = !!profile.privacy_accepted_at && (profile.privacy_version ?? '') !== LEGAL_VERSIONS.privacy.version;
  const termsReaccept = termsMismatch && (LEGAL_VERSIONS.terms.reacceptMode as ReacceptMode) === 'material';
  const privacyReaccept = privacyMismatch && (LEGAL_VERSIONS.privacy.reacceptMode as ReacceptMode) === 'material';

  return {
    needsFirstRunAcceptance: needsFirstRun,
    termsMismatch,
    privacyMismatch,
    termsRequiresReaccept: termsReaccept,
    privacyRequiresReaccept: privacyReaccept,
    anyMaterialReacceptRequired: termsReaccept || privacyReaccept,
    anyNoticePending: (termsMismatch && !termsReaccept) || (privacyMismatch && !privacyReaccept),
  };
}

/** For member soft-blocking only. Org admins are hard-blocked at ProtectedRoute. */
export function requiresMaterialAcceptance(
  profile: Parameters<typeof getLegalStatus>[0],
  isOrgAdmin: boolean
): boolean {
  const status = getLegalStatus(profile);
  if (status.needsFirstRunAcceptance) return true;
  return !isOrgAdmin && status.anyMaterialReacceptRequired;
}
